import { Grid, Skeleton, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FinanceType } from '../../../services/dtos/finances.dto';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import {
    RelationGeneralDataByTownDto,
    RelationGeneralDataDto,
    RelationGeneralDataTableDto,
    RelationType,
} from '../../../services/dtos/relation.dto';
import { getRelationGeneralData, getRelationGeneralDataByTown } from '../../../services/relation.service';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import HouseIcon from '@mui/icons-material/House';
import GroupsIcon from '@mui/icons-material/Groups';
import NoData from '../../commun/no-data';
import StyledTable from '../../commun/table';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';

/**
 * Ui pour la page d'assemblée générale
 * @constructor
 */
export default function GeneralDataRelationUi(
    props: Readonly<{
        mandate?: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        type: FinanceType | RelationType;
        handlerSetType: (newFinance: FinanceType | RelationType | undefined, text: string | undefined) => void;
    }>
) {
    // Document du meeting à voir et télécharger
    const [generalData, setGeneralData] = useState<RelationGeneralDataDto>();
    // Document du meeting à voir et télécharger
    const [dataByTown, setGeneralDateByTown] = useState<ResultSearchDto<RelationGeneralDataTableDto>>();
    // Page affichée deuxieme tableau
    const [page, setPage] = useState(0);
    // items par page deuxieme tableau
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // loader du meeting à voir et télécharger
    const [loading, setLoading] = useState(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    // Création des cellules têtes du tableau
    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<RelationGeneralDataTableDto, keyof RelationGeneralDataTableDto>[] = [
        { id: 'city', label: 'Collectivités', sort: false, width: 50 },
        { id: 'countResidents', label: "Nombre d'habitants", sort: false, width: 25 },
        { id: 'countSubscribers', label: "Nombre d'abonnés", sort: false, width: 25 },
    ];

    //Méthode au changement de la page
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setItemsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
    };
    /**
     * Demandes par années à l'ouverture de la page
     */
    useEffect(() => {
        let isMounted = true;
        if (props.mandate && isMounted) {
            setLoading(true);
            getRelationGeneralData(props.mandate.id)
                .then(async result => {
                    if (result && isMounted) {
                        // Conversion du contenu en pdf et ouverture de celui-ci
                        setGeneralData(result);
                    }
                })
                .catch(error => {
                    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
                        return;
                    }
                    console.error('Erreur lors de la récupération des documents');
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    if (isMounted) {
                        setLoading(false);
                    }
                });
        }

        return () => {
            isMounted = false;
        };
    }, [props.mandate, showMessage]);

    /**
     * Demandes par communes à l'ouverture de la page, puis à chauqe changement de page ou de itemsParPage
     */
    useEffect(() => {
        let isMounted = true;

        if (props.mandate && isMounted) {
            setLoading(true);
            getRelationGeneralDataByTown(props.mandate.id, page, itemsPerPage)
                .then(async result => {
                    if (result) {
                        const items: RelationGeneralDataTableDto[] = result.items.map(
                            (item: RelationGeneralDataByTownDto) => ({
                                city: item.city,
                                countResidents: item.generalDataCount.countResidents.toString(),
                                countSubscribers: item.generalDataCount.countSubscribers.toString(),
                            })
                        );
                        setGeneralDateByTown({
                            ...result,
                            items,
                        });
                    }
                })
                .catch(error => {
                    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
                        return;
                    }
                    console.error('Erreur lors de la récupération des documents');
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    if (isMounted) {
                        setLoading(false);
                    }
                });
        }
        return () => {
            isMounted = false;
        };
    }, [page, itemsPerPage, props.mandate, showMessage]);

    //Mise en place du tableau reunion passées
    const showTableRequests = () => {
        if (dataByTown === undefined) {
            // Affichage du skeleton le temps de recevoir les données
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        if (dataByTown.totalItems === 0) {
            // Si pas de données on envoit no data
            return <NoData title={'Information : '} content={'Aucune information à venir'} />;
        } else {
            return (
                // Table stylisée
                <StyledTable
                    data={dataByTown.items}
                    searching={loading}
                    headCells={headCells}
                    totalItems={dataByTown.totalItems}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeItemsPerPage}
                    itemsPerPage={itemsPerPage}
                ></StyledTable>
            );
        }
    };

    //Retourne la vue
    return (
        <Grid container className={'relation-request-content'} spacing={2}>
            <Grid item xs={12}>
                {/* Afffichage des données pour l'année en cours */}
                {generalData && (
                    <Grid container spacing={2}>
                        {generalData?.countResidents !== undefined && (
                            <Grid item md={6} xs={12} className="relationMetricCard">
                                <HouseIcon className="relationMetricCard__icon" />
                                <Typography className="relationMetricCard__label" variant={'body1'}>
                                    Nombre d'habitants :
                                </Typography>
                                <Typography className="relationMetricCard__value" variant={'body1'}>
                                    {generalData?.countResidents} habitants
                                </Typography>
                            </Grid>
                        )}
                        {generalData?.countSubscribers !== undefined && (
                            <Grid item md={6} xs={12} className="relationMetricCard">
                                <GroupsIcon className="relationMetricCard__icon" />
                                <Typography className="relationMetricCard__label" variant={'body1'}>
                                    Nombre d'abonnés déclarés en {new Date().getFullYear()} :
                                </Typography>
                                <Typography className="relationMetricCard__value" variant={'body1'}>
                                    {generalData?.countSubscribers} abonnés
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Grid>
            {/* Affichage du tableau des communes */}
            <Grid item xs={12}>
                <Typography className="relationUi__sectionTitle" variant={'h5'}>
                    Détail par collectivité
                </Typography>
            </Grid>
            <Grid item xs={12}>
                {/* Tableau des communes */}
                {showTableRequests()}
            </Grid>
        </Grid>
    );
}
